/**
 * formatFR.js — Helpers de formatage dates/heures en français
 * Utilisé par toutes les vues coaching humain.
 */

/**
 * Convert Date to local YYYY-MM-DD string (timezone-safe, no UTC shift)
 */
export function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

const MOIS_COURT = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

/**
 * "2026-04-18" → "18/04/2026" (format standard JJ/MM/AAAA pour listes/cartes/historiques)
 */
export function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/**
 * "2026-04-18" → "18 avril 2026" (format long pour titres de section)
 */
export function formatDateLongFR(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const day = parseInt(parts[2], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parts[0];
  return `${day} ${MOIS[month] || ''} ${year}`;
}

const JOURS_SEMAINE = [
  'dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'
];

/**
 * "2026-04-18" → "Vendredi 18 avril 2026" (format long avec jour de la semaine)
 */
export function formatDateWeekdayFR(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  const date = new Date(y, m, d);
  const weekday = JOURS_SEMAINE[date.getDay()] || '';
  const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return `${capitalized} ${d} ${MOIS[m] || ''} ${y}`;
}

/**
 * "2026-04-18" → "18 avr. 2026"
 */
export function formatDateShortFR(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const day = parseInt(parts[2], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parts[0];
  return `${day} ${MOIS_COURT[month] || ''} ${year}`;
}

/**
 * "09:00" → "09h00"
 */
export function formatTimeFR(timeStr) {
  if (!timeStr) return '';
  return timeStr.replace(':', 'h');
}

/**
 * "09:00", "10:00" → "09h00 – 10h00"
 */
export function formatTimeRangeFR(start, end) {
  return `${formatTimeFR(start)} – ${formatTimeFR(end)}`;
}

/**
 * Status badge mapping for bookings
 */
export function getStatusLabel(status) {
  const map = {
    pending: 'En attente',
    pending_payment: 'Paiement en attente',
    confirmed: 'Confirmée',
    completed: 'Terminée',
    cancelled: 'Annulée',
    to_pay: 'À payer',
    coach_paid: 'Payé',
    report_generated: 'Rapport généré',
    validated: 'Validée',
  };
  return map[status] || 'En cours';
}

export function getStatusStyle(status) {
  const map = {
    pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    pending_payment: 'bg-amber-50 text-amber-700 border border-amber-200',
    confirmed: 'bg-green-50 text-green-700 border border-green-200',
    completed: 'bg-blue-50 text-blue-700 border border-blue-200',
    cancelled: 'bg-red-50 text-red-600 border border-red-200',
    to_pay: 'bg-orange-50 text-orange-700 border border-orange-200',
    coach_paid: 'bg-green-50 text-green-700 border border-green-200',
    report_generated: 'bg-blue-50 text-blue-700 border border-blue-200',
    validated: 'bg-blue-50 text-blue-700 border border-blue-200',
  };
  return map[status] || 'bg-gray-50 text-gray-600 border border-gray-200';
}
